import { useState } from "react";
import { ActionList, ActionMenu, Button, Stack, Text } from "@primer/react";
import { FilterKey, FilterOption } from "../../../types/common/filters";
import { ChevronRightIcon, ChevronLeftIcon } from '@primer/octicons-react';
import { SelectFormModal } from "../../metrics/components/SelectFormModal";

interface IntermediateFilterScreenProps {
    opened: boolean;
    setOpened: (value: boolean) => void;
    title: string;
    items: Array<{
        key: FilterKey;
        label: string;
        labelSearchPlaceholder: string;
        selectedLabel: string;
        onSelect: () => void;
    }>;
    onDismiss: () => void;
    loadedOptions: Record<FilterKey, FilterOption[]>;
    selectedValues: Record<FilterKey, Set<string>>;
    handleSelect: (filterKey: FilterKey, value: string) => void;
    filterHasMore: Record<FilterKey, boolean>;
    filterLoadingMore: Record<FilterKey, boolean>;
    handleLoadMore: (filterKey: FilterKey) => void;
    loadingStates: Record<FilterKey, boolean>;
    resetFilterData: (filterKey: FilterKey) => void;
    loadFilterOptions: (filterKey: FilterKey, loadMore?: boolean, forceReload?: boolean) => Promise<void>;
    onSearch?: (filterKey: FilterKey, query: string) => void;
}

export const IntermediateFilterScreen: React.FC<IntermediateFilterScreenProps> = ({
    opened,
    setOpened,
    title,
    items,
    onDismiss,
    loadedOptions,
    selectedValues,
    handleSelect,
    filterHasMore,
    filterLoadingMore,
    handleLoadMore,
    onSearch,
    loadingStates,
    resetFilterData,
    loadFilterOptions
}) => {
    const [detailedSelectFilterOpened, setDetailedSelectFilterOpened] = useState<string | null>(null);

    const selectDetailedModalDidDismiss = () => {
        setDetailedSelectFilterOpened(null);
        //setOpened(true);
    };

    const handleItemSelect = (item: { key: FilterKey; onSelect: () => void }) => {
        // НЕ закрываем промежуточный экран
        setDetailedSelectFilterOpened(item.key);
        item.onSelect();

        // Для городов всегда перезагружаем данные при наличии выбранной страны
        if (item.key === 'cityResponseForName' ||
            item.key === 'cityResponseForId' ||
            item.key === 'category' ||
            item.key === 'subcategory') {
            loadFilterOptions(item.key, false, true);
        } else {
            // Для остальных фильтров загружаем данные только если их нет
            const options = loadedOptions[item.key] || [];
            if (options.length === 0) {
                loadFilterOptions(item.key, false, true);
            }
        }
    };

    return (
        <>
            {items.map((item) => {
                const itemOptions = loadedOptions[item.key] || [];
                const isLoading = loadingStates[item.key];
                const currentSelectedValues = selectedValues[item.key] || new Set<string>([]);
                return (
                    <SelectFormModal
                        key={item.key + '-modal'}
                        selectFilterOpened={detailedSelectFilterOpened === item.key}
                        setSelectFilterOpened={(isOpen) => setDetailedSelectFilterOpened(isOpen ? item.key : null)}
                        label={item.label}
                        searchPlaceholder={item.labelSearchPlaceholder}
                        items={itemOptions}
                        selectedValues={currentSelectedValues}
                        onSelect={(value) => {
                            handleSelect(item.key, value);
                            setDetailedSelectFilterOpened(item.key);
                        }}
                        onDismiss={selectDetailedModalDidDismiss}
                        isLoading={isLoading}
                        hasMore={filterHasMore[item.key]}
                        isLoadingMore={filterLoadingMore[item.key]}
                        onLoadMore={() => handleLoadMore(item.key)}
                        onSearch={onSearch ? (query) => onSearch(item.key, query) : undefined}
                    />
                );
            })}

            <ActionMenu open={opened} onOpenChange={setOpened}>
                <ActionMenu.Button className="w-8 h-8 right-[16px] absolute ml-auto invisible"></ActionMenu.Button>
                <ActionMenu.Overlay className="!w-[260px]"
                >
                    <ActionList>
                        <Stack justify="space-between" direction="horizontal">
                            <Stack direction="horizontal">
                                <Button
                                    leadingVisual={ChevronLeftIcon}
                                    variant='invisible'
                                    onClick={onDismiss}>
                                    <Text className='block mb-[1.5px]'>{title}</Text>
                                </Button>
                            </Stack>
                        </Stack>
                        {items.map((item) => (
                            <ActionList.Item
                                key={item.key}
                                onSelect={(e) => {
                                    e.preventDefault();
                                    handleItemSelect(item);
                                }}
                            >
                                <p className='font-normal'>{item.label}</p>
                                <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                <ActionList.Description variant="block">
                                    {item.selectedLabel}
                                </ActionList.Description>
                            </ActionList.Item>
                        ))}
                    </ActionList>
                </ActionMenu.Overlay>
            </ActionMenu>
        </>
    );
}